import classNames from "classnames";
import { useRef, useState, useEffect } from "react";
import ChevronDownIcon from "../../assets/icons/chveron-down";
import ClearIcon from "../../assets/icons/clear";
import { useOutsideClick } from "../../hooks/outsideClick";
import AppCheckbox from "../AppCheckbox";
import "./AppSelect.css";
import "flag-icons/css/flag-icons.min.css";

type Option = {
  name: string | number;
  value: string | number;
};

type AppSelectProps = {
  value?: (string | number)[];
  options: Option[];
  name?: string;
  placeholder?: string;
  onSelect?: (values: (string | number)[]) => void;
  onClose?: (name?: string) => void;
  isDark?: boolean;
  numerical?: boolean;
  searchable?: boolean;
  secondary?: boolean;
  single?: boolean;
  noFlag?: boolean;
  wide?: boolean;
  formatOptionLabel?: (option: Option) => React.ReactNode;
  noAdditionalName?: boolean;
};

type AppSelectDropDownProps = {
  options: Option[];
  onSelect: (values: (string | number)[]) => void;
  selectedValues: (string | number)[];
  onClose?: () => void;
  isOpened: boolean;
  isDark?: boolean;
  search?: boolean;
  secondary?: boolean;
  single?: boolean;
  noFlag?: boolean;
  wide?: boolean;
  formatOptionLabel?: (option: Option) => React.ReactNode;
};

const AppSelectDropDown = ({
  options = [],
  onSelect,
  selectedValues,
  onClose,
  isOpened,
  isDark,
  search,
  secondary,
  single,
  noFlag,
  wide,
  formatOptionLabel,
}: AppSelectDropDownProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleCheckboxChange = (value: string | number) => {
    if (single) {
      onSelect([value]);
      onClose?.();
    } else {
      const updatedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onSelect(updatedValues);
    }
  };

  const filtered = options.filter((option) =>
    option.name.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (isOpened && dropdownRef.current) {
      dropdownRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isOpened]);

  return (
    <div
      ref={dropdownRef}
      className={classNames("app-select-dropdown", {
        "app-select-dropdown--active": isOpened,
        "app-select-dropdown--secondary": secondary,
        "app-select-dropdown--wide": wide,
        "app-select-dropdown--dark": isDark,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      {search && isOpened && (
        <input
          type="text"
          className="app-select-search"
          placeholder="Search country"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      )}

      <div className="app-select-options">
        {filtered.length > 0 ? (
          filtered.map((option) => (
            <AppCheckbox
              name={option.name.toString()}
              key={option.value}
              checked={selectedValues.includes(option.value)}
              value={option.value}
              onChange={() => handleCheckboxChange(option.value)}
              wide={wide}
            >
              <span className="app-select-flag-option">
                {search && !noFlag && (
                  <span
                    className={`fi fi-${option.value.toString().toLowerCase()}`}
                  ></span>
                )}
                {formatOptionLabel ? formatOptionLabel(option) : option.name}
              </span>
            </AppCheckbox>
          ))
        ) : (
          <div className="app-select-empty">No Options</div>
        )}
      </div>
    </div>
  );
};

export default function AppSelect({
  value = [],
  options = [],
  name,
  placeholder = "Select",
  onSelect,
  onClose,
  numerical = false,
  searchable = false,
  secondary = false,
  single = false,
  noFlag = false,
  wide = false,
  isDark = false,
  formatOptionLabel,
  noAdditionalName,
}: AppSelectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [selectedValues, setSelectedValues] =
    useState<(string | number)[]>(value);

  const filteredOptions = options.filter((option) => !!option.name);

  const reset = () => {
    setIsOpened(false);
    onClose?.(name);
  };

  const toggleDropdown = () => setIsOpened((prev) => !prev);

  const handleSelect = (updatedValues: (string | number)[]) => {
    setSelectedValues(updatedValues);
    onSelect?.(updatedValues);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onSelect?.([]);
  };

  useOutsideClick(containerRef, reset);

  const selectedDisplay = (() => {
    if (selectedValues.length === 0) return placeholder;

    const selectedOption = filteredOptions.find(
      (option) => option.value === selectedValues[0]
    );

    if (isDark && selectedOption && !noAdditionalName) {
      return (
        <>
          {placeholder} {`(${selectedOption.name})`}
        </>
      );
    }

    if (single && selectedOption) {
      const flag = (
        <span
          className={`fi fi-${selectedOption.value.toString().toLowerCase()}`}
          style={{ marginRight: 8 }}
        />
      );
      return (
        <span className="app-select-single-display">
          {!noFlag && flag}
          {selectedOption.name} {!noFlag && `(${selectedOption.value})`}
        </span>
      );
    }

    if (numerical) {
      if (selectedValues.length > 1) {
        return `${placeholder.replace("All ", " ").replace(/s$/, "")}s (${
          selectedValues.length
        })`;
      }
    }

    return filteredOptions
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.name)
      .join("/");
  })();

  return (
    <div ref={containerRef} className="app-select-container">
      <button
        type="button"
        className={classNames("app-select-trigger", {
          "app-select-trigger--secondary": secondary,
          "app-select-trigger--wide": wide,
          "app-select-trigger--dark": isDark,
        })}
        onClick={toggleDropdown}
        aria-label="Select"
      >
        <div className="app-select-body">
          <div
            className={classNames("app-select-placeholder", {
              "app-select-placeholder--dark": isDark,
            })}
          >
            {selectedDisplay || placeholder}
          </div>
        </div>
        {!single && selectedValues.length > 0 && (
          <div
            className={classNames("app-select-clear-button", {
              "app-select-clear-button--dark": isDark,
            })}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <ClearIcon />
          </div>
        )}
        <div
          className={classNames("app-select-icon", {
            "app-select-icon--active": isOpened,
          })}
        >
          <ChevronDownIcon />
        </div>
      </button>

      <AppSelectDropDown
        options={filteredOptions}
        selectedValues={selectedValues}
        onSelect={handleSelect}
        onClose={reset}
        isOpened={isOpened}
        search={searchable}
        secondary={secondary}
        single={single}
        noFlag={noFlag}
        wide={wide}
        formatOptionLabel={formatOptionLabel}
        isDark={isDark}
      />
    </div>
  );
}
